import type { AthleteProfile, AthleteProfileInput } from '@gd/shared'
import { daysBetween } from '@gd/shared/dates'

/** Limits that match the server's profile schema (packages/api/src/account-routes.ts). */
export const PROFILE_LIMITS = {
  displayName: 60,
  location: 80,
  bio: 280,
  goalRace: 80,
  focusTags: 6,
  focusTag: 24,
} as const

// The database seeds each athlete with the light-mode default hex. A row that still carries it
// resolves to the theme token, so dark mode gets its own validated step.
const LIGHT_DEFAULTS: Record<string, string> = {
  '#e4572e': 'var(--athlete-a)',
  '#4f46e5': 'var(--athlete-b)',
}

/** An athlete's stored colour as a CSS value that follows the theme. */
export function athleteColor(stored: string | null | undefined): string {
  if (!stored) return 'var(--athlete-a)'
  return LIGHT_DEFAULTS[stored.toLowerCase()] ?? stored
}

/** "Skyler Fox" -> "SF", "Training partner" -> "TP", "sky" -> "S". */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = words.slice(0, 2).map((w) => Array.from(w)[0] ?? '')
  return letters.join('').toUpperCase() || '?'
}

export interface GoalCountdown {
  /** The figure set large: weeks (or days in the last fortnight). Null on or after race day. */
  value: number | null
  unit: 'weeks' | 'week' | 'days' | 'day' | null
  /** Plain words for the whole thing, e.g. "34 weeks to go", "Race day", "Raced". */
  label: string
}

/** Weeks (whole, rounded down) to a goal date; days inside the last two weeks. */
export function goalCountdown(goalDate: string, today: string): GoalCountdown {
  const days = daysBetween(today, goalDate)
  if (days < 0) return { value: null, unit: null, label: 'Raced' }
  if (days === 0) return { value: null, unit: null, label: 'Race day' }
  if (days < 14) {
    const unit = days === 1 ? 'day' : 'days'
    return { value: days, unit, label: `${days} ${unit} to go` }
  }
  const weeks = Math.floor(days / 7)
  const unit = weeks === 1 ? 'week' : 'weeks'
  return { value: weeks, unit, label: `${weeks} ${unit} to go` }
}

/** A field-keyed list of problems with a profile draft, in the server's terms but plain words. */
export type ProfileErrors = Partial<Record<keyof AthleteProfileInput, string>>

export function validateProfile(input: AthleteProfileInput): ProfileErrors {
  const errors: ProfileErrors = {}
  const tooLong = (v: string | null, max: number) => (v?.trim().length ?? 0) > max
  if (tooLong(input.displayName, PROFILE_LIMITS.displayName))
    errors.displayName = `Keep it to ${PROFILE_LIMITS.displayName} characters`
  if (tooLong(input.location, PROFILE_LIMITS.location))
    errors.location = `Keep it to ${PROFILE_LIMITS.location} characters`
  if (tooLong(input.goalRace, PROFILE_LIMITS.goalRace))
    errors.goalRace = `Keep it to ${PROFILE_LIMITS.goalRace} characters`
  if (tooLong(input.bio, PROFILE_LIMITS.bio))
    errors.bio = `Keep it to ${PROFILE_LIMITS.bio} characters`
  if (input.goalDate && !/^\d{4}-\d{2}-\d{2}$/.test(input.goalDate)) errors.goalDate = 'Pick a date'
  if (input.focus.length > PROFILE_LIMITS.focusTags)
    errors.focus = `Up to ${PROFILE_LIMITS.focusTags} tags`
  else if (input.focus.some((t) => t.length > PROFILE_LIMITS.focusTag))
    errors.focus = `Keep each tag to ${PROFILE_LIMITS.focusTag} characters`
  if (input.avatarUrl) {
    let ok = false
    try {
      ok = new URL(input.avatarUrl).protocol === 'https:'
    } catch {
      ok = false
    }
    if (!ok) errors.avatarUrl = 'Use a link that starts with https://'
  }
  return errors
}

/** Trim every text field and turn blanks into null, as the server does. */
export function cleanProfile(input: AthleteProfileInput): AthleteProfileInput {
  const t = (v: string | null) => {
    const s = v?.trim() ?? ''
    return s ? s : null
  }
  return {
    displayName: t(input.displayName),
    location: t(input.location),
    bio: t(input.bio),
    goalRace: t(input.goalRace),
    goalDate: t(input.goalDate),
    focus: input.focus.map((f) => f.trim()).filter(Boolean),
    avatarUrl: t(input.avatarUrl),
  }
}

export const EMPTY_PROFILE_INPUT: AthleteProfileInput = {
  displayName: null,
  location: null,
  bio: null,
  goalRace: null,
  goalDate: null,
  focus: [],
  avatarUrl: null,
}

/** The form's own copy of a profile: every text field a string, so inputs stay controlled. */
export interface ProfileDraft {
  displayName: string
  location: string
  bio: string
  goalRace: string
  goalDate: string
  focus: string[]
  avatarUrl: string
}

export function toDraft(
  profile: Partial<AthleteProfile> | null | undefined,
  fallbackName?: string,
): ProfileDraft {
  return {
    displayName: profile?.displayName ?? fallbackName ?? '',
    location: profile?.location ?? '',
    bio: profile?.bio ?? '',
    goalRace: profile?.goalRace ?? '',
    goalDate: profile?.goalDate ?? '',
    focus: profile?.focus ?? [],
    avatarUrl: profile?.avatarUrl ?? '',
  }
}

export function fromDraft(draft: ProfileDraft): AthleteProfileInput {
  return cleanProfile({ ...draft })
}
